import { useEffect, useState } from "react"
import type { TProcessingStage } from "../../lib/pipeline-progress"
import { FinalizationProgressView } from "./FinalizationProgressView"
import {
  activeStepIndex,
  nextStepPercent,
  resolveDisplayedStage,
  resolveFinalizeMode,
  stepProgressRange,
  type TFinalizeMode,
} from "./progress"

// Canonical home is `lib/pipeline-progress`; re-exported here so existing
// consumers keep importing the stage type from FinalizationProgress.
export type { TFinalizeMode, TProcessingStage }

interface IProps {
  /** Current processing stage. `null` starts at "Saving the audio". */
  stage: TProcessingStage | null
  /**
   * Accepted so existing callers keep compiling. The bar is step-based now
   * and does not show this number.
   */
  progress?: number | null
  /**
   * Accepted so existing callers keep compiling. Pipeline step names
   * ("Speaker Resolution", "Cluster Chapters") are never shown.
   */
  currentStepName?: string | null
  /** Backend step id (`speaker-resolution`, `cluster-chapters`). Never a display name. */
  currentStepRef?: string | null
  /** Full keeps the summary step. Lite's last step is the title. */
  mode?: TFinalizeMode | null
  /** `meeting-lite` selects Lite when `mode` is omitted. */
  templateId?: string | null
  /** Voices found so far. Shown under the voices step when greater than 0. */
  voiceCount?: number
  /** Transcript lines so far. Shown under the transcript step when greater than 0. */
  segmentCount?: number
}

function useStepPercent(stepIndex: number, total: number, isComplete: boolean): number {
  const [display, setDisplay] = useState<number>(() =>
    isComplete ? 100 : stepProgressRange(stepIndex, total, false).start,
  )

  useEffect(() => {
    const range = stepProgressRange(stepIndex, total, isComplete)
    if (isComplete) {
      setDisplay(100)
      return
    }
    setDisplay((current) => nextStepPercent(current, range, false, 0))
    const id = setInterval(() => {
      setDisplay((current) => nextStepPercent(current, range, false))
    }, 350)
    return () => clearInterval(id)
  }, [isComplete, stepIndex, total])

  return display
}

function useElapsedSeconds(isComplete: boolean): number {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (isComplete) return
    const id = setInterval(() => setElapsed((value) => value + 1), 1000)
    return () => clearInterval(id)
  }, [isComplete])
  return elapsed
}

export function FinalizationProgress({
  stage,
  currentStepRef,
  mode,
  templateId,
  voiceCount,
  segmentCount,
}: IProps): React.JSX.Element {
  const resolvedMode = resolveFinalizeMode({ mode, templateId })
  const displayed = resolveDisplayedStage(stage, resolvedMode, currentStepRef)
  const position = activeStepIndex(displayed, resolvedMode)
  const percent = useStepPercent(position.index, position.total, position.isComplete)
  const elapsedSeconds = useElapsedSeconds(position.isComplete)

  return (
    <FinalizationProgressView
      mode={resolvedMode}
      stepIndex={position.index}
      percent={percent}
      isComplete={position.isComplete}
      elapsedSeconds={elapsedSeconds}
      voiceCount={voiceCount}
      segmentCount={segmentCount}
    />
  )
}
